import { CircularProgress, Collapse, ThemeProvider } from "@material-ui/core";
import {
  AutoComplete,
  AutoCompleteOption,
  AutoCompleteRequest,
} from "../Controls/Autocomplete";
import { SFErrorState, parseSFError } from "../Salesforce/SFErrorState";
import { SalesforceLogin } from "../Salesforce/SalesforceLogin";
import { groopTheme } from "../styles/MaterialGroopTheme";
import {
  ErrorDTO,
  SFConnectionDTO,
  SFTargetDTO,
  useGroopitApiContext,
} from "groopit-js";
import React, { useEffect, useState } from "react";
import { ComponentType } from "groopit-js/client";

export interface InheritedDataEditorViewModel {
  ComponentModel_Id: number;
  Type: ComponentType;
  Required: boolean;
  Description: string;
  HiddenFromMembers: boolean;
  ParentComponent?: ComponentType;
  ExternalFieldName: string;
  ExternalFieldLabel: string;
  DisplayValue: string;
  Value: string;
}

interface SaveToSalesforceDataEditorProps {
  baseName: string;
  assignmentId: string;
  componentId: number;
  description: string;
  connection?: SFConnectionDTO;
  hasInheritedData: boolean;
  primaryDataType: string;
  primaryDataLabel: string;
  primaryDataFieldName: string;
  primaryDataId?: string;
  primaryDataDisplayName?: string;
  inheritedData?: InheritedDataEditorViewModel[];
  disabled: boolean;
}

export const SaveToSalesforceDataEditor = (
  props: SaveToSalesforceDataEditorProps,
): JSX.Element => {
  const api = useGroopitApiContext();
  const [associatedObject, setAssociatedObject] = useState<SFTargetDTO>();
  const [error, setError] = useState<SFErrorState>();
  const [selectedOption, setSelectedOption] = useState(
    props.primaryDataId && props.primaryDataDisplayName
      ? {
          value: props.primaryDataId,
          label: props.primaryDataDisplayName,
        }
      : undefined,
  );
  const [inheritedData, setInheritedData] = useState(props.inheritedData);
  const [inheritedDataError, setInheritedDataError] = useState<SFErrorState>();
  const [expanded, setExpanded] = useState(props.primaryDataId ? true : false);

  // Inherited data follows the SaveToSalesforceData so we just need to parse
  // the index number from the SaveToSalesforce data's name, then increment it
  // for each inherited data.
  const getInheritedDataBaseName = (index: number) => {
    const saveToSalesforceIndex = /.*\[(\d+)\]/g.exec(props.baseName)!;
    const inheritedDataIndex = parseInt(saveToSalesforceIndex[1]) + index + 1;
    return `${props.baseName.slice(
      0,
      props.baseName.indexOf("Components"),
    )}Components[${inheritedDataIndex}]`;
  };

  const fetchRelatedObject = () => {
    if (props.connection) {
      setError(undefined);
      api.groopit
        .getExternalServiceRelatedObject(
          props.connection.id,
          props.primaryDataFieldName,
        )
        .then((target: SFTargetDTO) => {
          setAssociatedObject(target);
        })
        .catch((error: ErrorDTO) => {
          setError(parseSFError(error));
        });
    }
  };

  const fetchRelatedRecords = (
    request: AutoCompleteRequest,
    response: (data: AutoCompleteOption[]) => void,
  ) => {
    if (props.connection) {
      api.groopit
        .getExternalServiceRelatedRecordsV2(
          props.assignmentId,
          props.componentId,
          request.term,
          100,
        )
        .then((records) => {
          response(
            records.map((record) => {
              return {
                value: record.id!,
                label: record.name!,
              };
            }),
          );
        })
        .catch(() => {
          // TODO We don't really handle errors here
        });
    }
  };

  const onPrimaryDataSelected = (primaryData?: AutoCompleteOption) => {
    setSelectedOption(primaryData);
    refreshInheritedData(primaryData);
  };

  const refreshInheritedData = (selectedOption?: AutoCompleteOption) => {
    if (props.hasInheritedData) {
      setInheritedData(undefined);
      let previousInheritedData: InheritedDataEditorViewModel[];
      if (inheritedData) {
        previousInheritedData = [...inheritedData];
      }
      setInheritedDataError(undefined);
      if (selectedOption) {
        api.assignment
          .getInheritedData(
            props.assignmentId,
            props.componentId,
            selectedOption.value,
          )
          .then((data) => {
            setInheritedData(
              data.map((d) => {
                return {
                  ComponentModel_Id: d.model.componentModel_Id!,
                  Type: d.type!,
                  Required: d.model.required!,
                  Description: d.model.description!,
                  HiddenFromMembers: d.model.hiddenFromMembers!,
                  ParentComponent: ComponentType.SaveToSalesforce,
                  ExternalFieldName: d.model.externalFieldName!,
                  ExternalFieldLabel: d.model.externalFieldLabel!,
                  DisplayValue: d.displayValue!,
                  Value: d.value!,
                };
              }),
            );
          })
          .catch((error: ErrorDTO) => {
            console.log(error);
            setInheritedData(previousInheritedData);
            setInheritedDataError(parseSFError(error));
          });
      }
    }
  };

  const toggleExpanded = () => {
    if (expanded) {
      setExpanded(false);
      setSelectedOption(undefined);
    } else {
      setExpanded(true);
    }
  };

  useEffect(() => {
    if (expanded && props.connection && !associatedObject && !error) {
      fetchRelatedObject();
    }
  }, [expanded]);

  return (
    <ThemeProvider theme={groopTheme}>
      <div className="flex" onClick={toggleExpanded}>
        <input
          type="hidden"
          name={`${props.baseName}.AssignmentId`}
          value={props.assignmentId}
        />
        <input
          type="hidden"
          name={`${props.baseName}.HasInheritedData`}
          value={props.hasInheritedData ? "True" : "False"}
        />
        <input
          type="hidden"
          name={`${props.baseName}.ExternalDataType`}
          value={props.primaryDataType}
        />
        <input
          type="hidden"
          name={`${props.baseName}.ExternalFieldName`}
          value={props.primaryDataFieldName}
        />
        <input
          type="hidden"
          name={`${props.baseName}.ExternalFieldLabel`}
          value={props.primaryDataLabel}
        />
        <input
          type="hidden"
          name={`${props.baseName}.SalesforceError`}
          value={error}
        />
        {/* The input normally handles this but if it's disabled then it won't submit */}
        {(props.disabled || !props.connection || !associatedObject) &&
          selectedOption && (
            <>
              <input
                type="hidden"
                name={`${props.baseName}.ExternalId`}
                value={selectedOption.value}
              />
              <input
                type="hidden"
                name={`${props.baseName}.DisplayName`}
                value={selectedOption.label}
              />
            </>
          )}
        {inheritedData &&
          inheritedData.map((data, index) => (
            <div key={data.ExternalFieldName}>
              <input
                type="hidden"
                name={`${getInheritedDataBaseName(index)}.ComponentModel_Id`}
                value={data.ComponentModel_Id}
              />
              <input
                type="hidden"
                name={`${getInheritedDataBaseName(index)}.Type`}
                value={ComponentType[data.Type]}
              />
              <input
                type="hidden"
                name={`${getInheritedDataBaseName(index)}.Required`}
                value={data.Required ? "True" : "False"}
              />
              <input
                type="hidden"
                name={`${getInheritedDataBaseName(index)}.Description`}
                value={data.Description}
              />
              <input
                type="hidden"
                name={`${getInheritedDataBaseName(index)}.HiddenFromMembers`}
                value={data.HiddenFromMembers ? "True" : "False"}
              />
              <input
                type="hidden"
                name={`${getInheritedDataBaseName(index)}.ParentComponent`}
                value={
                  data.ParentComponent
                    ? ComponentType[data.ParentComponent]
                    : ""
                }
              />
              <input
                type="hidden"
                name={`${getInheritedDataBaseName(index)}.ExternalFieldName`}
                value={data.ExternalFieldName}
              />
              <input
                type="hidden"
                name={`${getInheritedDataBaseName(index)}.ExternalFieldLabel`}
                value={data.ExternalFieldLabel}
              />
              <input
                type="hidden"
                name={`${getInheritedDataBaseName(index)}.DisplayValue`}
                value={data.DisplayValue}
              />
              <input
                type="hidden"
                name={`${getInheritedDataBaseName(index)}.Value`}
                value={data.Value}
              />
            </div>
          ))}
        <div className="flex-grow">{props.description}</div>
        {expanded ? (
          <div className="flex items-center w-3 h-6 filter-app-teal">
            <img
              className="align-middle w-full"
              src="/Content/Images/ic_checkbox_checked.svg"
              alt="Save to Salesforce"
            />
          </div>
        ) : (
          <div className="flex items-center w-3 h-6 filter-app-gray-03">
            <img
              className="align-middle w-full"
              src="/Content/Images/ic_checkbox_unchecked.svg"
              alt="Don't save to Salesforce"
            />
          </div>
        )}
      </div>
      <Collapse in={expanded}>
        {!props.connection && (
          <div className="text-error">
            Group no longer connected to Salesforce.
          </div>
        )}
        <AutoComplete
          label="Salesforce:"
          placeholder="Type to find"
          disabled={props.disabled || !props.connection || !associatedObject}
          minCharsToSearch={3}
          inputIdName={`${props.baseName}.ExternalId`}
          inputNameName={`${props.baseName}.DisplayName`}
          selectedOption={selectedOption}
          source={fetchRelatedRecords}
          onOptionSelected={onPrimaryDataSelected}
        />
        {selectedOption &&
          props.hasInheritedData &&
          props.connection &&
          (inheritedDataError ? (
            (inheritedDataError === SFErrorState.NeedsAuthentication && (
              <SalesforceLogin
                connectionId={props.connection.id}
                onLogin={() => refreshInheritedData(selectedOption)}
              ></SalesforceLogin>
            )) ||
            (inheritedDataError === SFErrorState.Forbidden && (
              <p className="text-display-normal text-color-secondary">
                Your account does not have permission to access Groopit within
                Salesforce. Please contact your Groopit administrator.
              </p>
            )) ||
            (inheritedDataError === SFErrorState.SFError && (
              <div className="flex">
                <p className="flex-grow text-display-normal text-color-secondary">
                  An error occurred when communicating with Salesforce. Please
                  try again later. If the problem persists, contact{" "}
                  <a href="mailto:help@groopit.co">help@groopit.co</a>.
                </p>
                <button
                  type="button"
                  onClick={() => refreshInheritedData(selectedOption)}
                >
                  <img
                    className="w-3"
                    src="/Content/Images/resend.png"
                    alt="Refresh"
                  />
                </button>
              </div>
            )) ||
            (inheritedDataError === SFErrorState.Unknown && (
              <div className="flex">
                <p className="flex-grow text-display-normal text-color-secondary">
                  An error occurred when communicating with Salesforce. Please
                  try again later. If the problem persists, contact{" "}
                  <a href="mailto:help@groopit.co">help@groopit.co</a>.
                </p>
                <button
                  type="button"
                  onClick={() => refreshInheritedData(selectedOption)}
                >
                  <img
                    className="w-3"
                    src="/Content/Images/resend.png"
                    alt="Refresh"
                  />
                </button>
              </div>
            ))
          ) : inheritedData ? (
            <div className="mt-2 flex">
              <div className="flex-grow">
                {inheritedData.map((data) => (
                  <div
                    key={data.ExternalFieldName}
                    className="text-display-caption"
                  >
                    {data.ExternalFieldLabel}: {data.DisplayValue}
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => refreshInheritedData(selectedOption)}
              >
                <img
                  className="w-3"
                  src="/Content/Images/resend.png"
                  alt="Refresh"
                />
              </button>
            </div>
          ) : (
            <div className="text-center mt-8">
              <CircularProgress />
            </div>
          ))}
        {(props.connection &&
          ((error === SFErrorState.NeedsAuthentication && (
            <SalesforceLogin
              connectionId={props.connection.id}
              onLogin={() => fetchRelatedObject()}
            ></SalesforceLogin>
          )) ||
            (error === SFErrorState.Forbidden && (
              <p className="text-display-normal text-color-secondary">
                Your account does not have permission to access Groopit within
                Salesforce. Please contact your Groopit administrator.
              </p>
            )) ||
            (error == SFErrorState.MissingField && (
              <p className="text-display-normal text-color-secondary">
                &quot;{props.primaryDataLabel}&quot; is missing from your
                Groopit Salesforce configuration. Please contact your Groopit
                administrator.
              </p>
            )) ||
            (error === SFErrorState.SFError && (
              <p className="text-display-normal text-color-secondary">
                An error occurred when communicating with Salesforce. Please try
                again later. If the problem persists, contact{" "}
                <a href="mailto:help@groopit.co">help@groopit.co</a>.
              </p>
            )))) ||
          (props.connection && !associatedObject && (
            <div className="text-center mt-8">
              <CircularProgress />
            </div>
          ))}
      </Collapse>
    </ThemeProvider>
  );
};
